<template>
  <div
    class="navigation-drawer-component"
    :class="{
      'navigation-drawer-component--collapsed': isCollapsed,
      'navigation-drawer-component--over': isExpandedOver,
    }"
  >
    <!-- Развёрнутый сайдбар на компактном десктопе лежит поверх страницы -->
    <v-overlay
      :value="isExpandedOver"
      z-index="6"
      @click.native="compactExpanded = false"
    />
    <v-navigation-drawer
      id="default-drawer"
      v-model="show"
      :mobile-breakpoint="1024"
      dark
      :right="$vuetify.rtl"
      mini-variant-width="80"
      app
      :width="drawerWidth"
    >
      <perfect-scrollbar :options="{ suppressScrollX: true }">
        <aside-logo />

        <v-btn
          v-if="isCompactScreen"
          class="button-toggle-aside"
          icon
          :title="compactExpanded ? 'Свернуть меню' : 'Развернуть меню'"
          @click="compactExpanded = !compactExpanded"
        >
          <font-awesome-icon :icon="compactExpanded ? 'chevron-left' : 'chevron-right'" />
        </v-btn>

        <aggregators-status-widget v-show="!isCollapsed" />

        <div class="clocks-section">
          <clocks />
        </div>

        <div class="new-order-section">
          <!-- При блокировке заказы недоступны — кнопка ведёт туда, где её снимают.
               Сотруднику без права на пополнение её тоже показываем: на странице
               его встретит заглушка «нет доступа», а не молчащая кнопка -->
          <v-btn
            v-if="billingBlocked && (canUseBilling || userCan(['crm.orders.edit']))"
            class="button-new-order button-new-order--blocked text-normalize"
            :title="isCollapsed ? 'Пополнить баланс' : undefined"
            @click="goToTopup"
          >
            <font-awesome-icon icon="plus" />
            <span class="button-new-order__label">Пополнить баланс</span>
          </v-btn>
          <v-btn
            v-else-if="userCan(['crm.orders.edit'])"
            class="button-new-order text-normalize"
            :title="isCollapsed ? 'Новый заказ' : undefined"
            @click="openNewOrder"
          >
            <font-awesome-icon icon="plus" />
            <span class="button-new-order__label">Новый заказ</span>
          </v-btn>
        </div>

        <links :collapsed="isCollapsed" />
        <!-- <default-account-settings />-->
        <!-- <v-divider class="mx-3 mb-2" />-->
        <!-- <default-list :items="items" />-->
      </perfect-scrollbar>
      <div v-show="!isCollapsed" class="navigation-drawer-component-powered">
        <img src="/img/logos/fact-logo-50.svg" alt="">
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import Clocks from './Clocks'
  import Links from './links/Links'
  import _ from 'lodash'
  import AsideLogo from '@/components/aside/AsideLogo'
  import AggregatorsStatusWidget from '@/components/aside/aggregators-status/AggregatorsStatusWidget'
  import RouteName from '@/models/enums/RouteName'
  import compactScreen from '@/mixins/compact-screen'

  // Ширина полоски с иконками на компактном десктопе
  const COLLAPSED_WIDTH = 80

  export default {
    name: 'DefaultDrawer',
    components: {
      AggregatorsStatusWidget,
      AsideLogo,
      Clocks,
      Links,
    },
    mixins: [compactScreen],
    data () {
      return {
        drawer: true,
        asideWidth: 326,
        compactExpanded: false,
      }
    },
    computed: {
      ...mapState({
        showMainAside: state => state.app.showMainAside,
      }),
      show: {
        get () {
          return this.showMainAside
        },
        set (value) {
          this.$store.commit('app/toggleAside', value)
        },
      },
      // На компактном десктопе сайдбар - полоска с иконками
      isCollapsed () {
        return this.isCompactScreen && !this.compactExpanded
      },
      isExpandedOver () {
        return this.isCompactScreen && this.compactExpanded
      },
      // Vuetify всегда считает сайдбар полоской и отступ страницы не меняет;
      // развёрнутую ширину даёт CSS, поэтому меню ложится поверх контента
      drawerWidth () {
        return this.isCompactScreen ? COLLAPSED_WIDTH : this.asideWidth
      },
    },
    watch: {
      $route () {
        this.compactExpanded = false
      },
      isCompactScreen () {
        this.compactExpanded = false
      },
    },
    mounted () {
      this.$nextTick(() => {
        this.show = window.innerWidth > 1024
        this.calcAsideWidth()
      })
    },
    created () {
      window.addEventListener('resize', this.calcAsideWidthOnResize)
    },
    destroyed () {
      window.removeEventListener('resize', this.calcAsideWidthOnResize)
    },
    methods: {
      calcAsideWidth () {
        this.asideWidth = window.innerWidth < 1900 ? 260 : 326
      },
      calcAsideWidthOnResize: _.debounce(function () {
        this.calcAsideWidth()
      }, 100),
      openNewOrder () {
        this.$router.push('/new-order')
      },
      goToTopup () {
        this.$router.push({ name: RouteName.BillingTopup })
      },
    },
  }
</script>

<style lang="less">
.navigation-drawer-component {
  .v-list-item {
    margin-bottom: 8px;
  }

  .v-navigation-drawer__content {
    height: 100% !important;
  }

  .theme--dark.v-navigation-drawer {
    background-color: #040404;
  }

  .v-list-item::before,
  .v-list-item::after {
    display: none;
  }

  .v-list-group__header__prepend-icon,
  .v-list-item__icon {
    margin-top: 12px;
    margin-bottom: 12px;
    margin-left: 4px;
  }

  &.v-navigation-drawer--mini-variant {
    .v-list-item {
      justify-content: flex-start !important;
    }
  }

  &-powered {
    padding: 20px 27px 10px 27px;

    img {
      max-width: 100%;
      width: 173px;
      height: auto;
    }
  }

  .ps {
    height: calc(100% - 63px);
  }

  // Полоска: логотип F.A.K.T. внизу скрыт, скролл на всю высоту
  &--collapsed .ps {
    height: 100%;
  }

  // Vuetify держит ширину полоски инлайном - развёрнутую задаём поверх
  &--over .v-navigation-drawer {
    width: 260px !important;
  }

  .v-navigation-drawer__content {
    height: auto;
    overflow-y: hidden;
  }

  // iOS Safari: inline 100vh от Vuetify (проп app) не учитывает адресную
  // строку - низ drawer'а (лого) уходит за экран. dvh пересчитывается
  // при показе/скрытии панели браузера.
  @supports (height: 100dvh) {
    .v-navigation-drawer--is-mobile {
      height: 100dvh !important;
    }
  }
}
</style>

<style lang="less" scoped>
//noinspection ALL
.navigation-drawer-component {
  .new-order-section {
    padding: 0 23px;
    .button-new-order {
      height: 65px !important;
      width: 100%;
      background-color: @color-primary !important;
      border-radius: 100px;
      font-weight: normal;
      font-size: 24px;
      // трекинг Vuetify (.0892857143em) растягивает надпись до краёв кнопки
      letter-spacing: normal;
      margin-bottom: 37px;

      svg {
        height: 16px;
        margin-right: 16px;
      }
    }

    // «Пополнить баланс» длиннее «Нового заказа» и в узкий drawer (260px) не влезает
    .button-new-order.button-new-order--blocked {
      background-color: @color-error !important;
      box-shadow: 0 0 0 4px fade(@color-error, 26%);
      font-size: 18px;

      svg {
        margin-right: 12px;
      }
    }

  }
  .media-lg({
    .new-order-section {
      display: none;
    }
  });

  .button-toggle-aside {
    display: flex;
    width: 44px;
    height: 44px;
    margin: 0 auto 14px;
    background-color: #191919;
    border-radius: 10px;

    // размер иконки FontAwesome считает от font-size
    svg {
      font-size: 20px;
      height: 1em;
      ::v-deep {
        .fill-svg(#ffffff);
      }
    }
  }

  // Полоска: логотип скрыт, кнопка - первый элемент
  &--collapsed .button-toggle-aside {
    margin: 12px auto 10px;
  }

  // Развёрнутый вид: кнопка сворачивания в строке логотипа
  &--over .button-toggle-aside {
    position: absolute;
    top: 14px;
    right: 14px;
    margin: 0;
  }

  // .media-xl-max({
    .new-order-section {
      .button-new-order {
        height: 52px !important;
        font-size: 20px;
        margin-bottom: 30px;
      }
    }

    &-powered {
      //padding: 15px;

      img {
        max-width: 100%;
        width: 260px;
        height: auto;
      }
    }

  // });

  &--collapsed .new-order-section {
    padding: 0;
    display: flex;
    justify-content: center;

    .button-new-order {
      width: 52px;
      min-width: 52px;
      padding: 0;
      margin-bottom: 14px;
    }

    // с --blocked: у красной кнопки свой отступ иконки, он специфичнее
    .button-new-order svg,
    .button-new-order.button-new-order--blocked svg {
      height: 20px;
      margin-right: 0;
    }

    .button-new-order__label {
      display: none;
    }
  }

  .media-sm({
    .v-list-item {
      min-height: 44px;
    }

    .v-navigation-drawer__content {
      padding-bottom: 24px;
    }
  });
}
</style>
